import React, { useCallback, useMemo, useRef } from 'react';
import '@ant-design/compatible/assets/index.css';
import { Modal, Select, InputNumber, Form, Input, message } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { connect } from 'react-redux';
import { getCurrency, nullSafeGet } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { ContractorPrivateNetworksRestCrudThunksForSupplier } from '../../thunks/contractors_private_networks_thunks';
import {
	removeSubContractWorkOrderForSupplier,
	subContractWorkOrderForSupplier,
	updateContractWorkOrder,
} from '../../thunks/work_orders_thunks';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import ModalFormFooter from '../common/ModalFormFooter';
import { CONTRACTOR_PROPS } from '../../utils/DataConstants';
import { isBuyerNteHiddenFromSupplier } from '../../utils/WorkOrderUtils';

const FORM_ID = 'subcontractor-form';
interface SubContractWorkOrderModalFormProps extends FormComponentProps {
	onCancel: any;
	onSuccess: any;
	createErrors: any;
	updateErrors: any;
	currentUser: any;
	supplierFacilities: any;
	fetchMultipleSupplierFacilitiesInPrivateNetwork: any;
	fetchSupplierFacilitiesInPrivateNetwork: any;
	creating: boolean;
	updating: boolean;
	formData: any;
	fetchContractors: any;
	contractorPrivateNetworks: any;
	addWarrantySupplier: any;
	subContractWorkOrder: any;
	removeSubContractWorkOrder: any;
	updateSubContractorInfo: any;
}

const SubContractWorkOrderModalForm: React.FC<SubContractWorkOrderModalFormProps> = ({
	onCancel,
	formData,
	contractorPrivateNetworks,
	fetchContractors,
	creating,
	updating,
	onSuccess,
	subContractWorkOrder,
	currentUser,
	removeSubContractWorkOrder,
	updateSubContractorInfo,
}): React.ReactElement => {
	const editorRef = useRef(null);

	const titleText = 'Subcontract Work Order';
	const workOrderCurrency = getCurrency({ workOrder: formData });

	const subContractorWorkOrder = nullSafeGet(CONTRACTOR_PROPS.TO_WO, formData);

	const okText = !!subContractorWorkOrder ? 'Update' : 'Subcontract';

	const required = nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.subSupplierFacilityId`, formData)
		? false
		: true;

	const initialValues = useMemo(
		() => ({
			updatedTitle:
				nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.updatedTitle`, formData) ||
				nullSafeGet(`${CONTRACTOR_PROPS.FROM_WO}.updatedTitle`, formData) ||
				nullSafeGet('title', formData),
			supplierFacilityId: nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.subSupplierFacilityId`, formData),
			subContractorNte: nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.updatedNte`, formData),
		}),
		[formData]
	);

	const updateOrSubContractWO = useCallback(
		(values) => {
			const action = !!subContractorWorkOrder ? updateSubContractorInfo : subContractWorkOrder;

			const updatedDescription = editorRef && editorRef.current && editorRef.current.getContent();

			const entity = {
				...(subContractorWorkOrder || {}),
				workOrderId: formData.id,
				updatedTitle: values.updatedTitle,
				updatedDescription,
				subSupplierFacilityId: values.supplierFacilityId,
				updatedNte: values.subContractorNte,
				contractorCreatedBy: currentUser.email,
			};

			action(entity).then(() => {
				onSuccess && onSuccess();
			});
		},
		[
			currentUser.email,
			formData.id,
			onSuccess,
			subContractWorkOrder,
			subContractorWorkOrder,
			updateSubContractorInfo,
		]
	);

	const removeSubcontractor = useCallback(() => {
		removeSubContractWorkOrder({
			id: formData.id,
		}).then(() => {
			message.success('Subcontractor removed successfully!');
			onSuccess && onSuccess(true);
		});
	}, [formData.id, onSuccess, removeSubContractWorkOrder]);

	const onSubmit = useCallback(
		(values) => (values.supplierFacilityId ? updateOrSubContractWO(values) : removeSubcontractor()),
		[removeSubcontractor, updateOrSubContractWO]
	);

	const initialDescription = useMemo(
		() =>
			nullSafeGet(`${CONTRACTOR_PROPS.TO_WO}.updatedDescription`, formData) ||
			nullSafeGet(`${CONTRACTOR_PROPS.FROM_WO}.updatedDescription`, formData) ||
			nullSafeGet('description', formData),
		[formData]
	);

	const subContractedFromNte = nullSafeGet(`${CONTRACTOR_PROPS.FROM_WO}.updatedNte`, formData);
	const clientNte = subContractedFromNte || nullSafeGet('nte', formData);
	const isClientNteHidden =
		!subContractedFromNte && isBuyerNteHiddenFromSupplier(formData, currentUser);

	// The private-network record for the already-assigned subcontractor is only in Redux if some
	// earlier page happened to fetch it. On a fresh load of this work order it is absent, and
	// OWAsyncSelect's `getEntityById` miss returns `{}` — enough to blow up any accessor that walks
	// into `supplierFacility`, which white-screened the page (the accessors above are now null-safe).
	// Seeding the current subcontractor as an extra option also keeps its name in the select instead
	// of an empty box while the contractor list loads.
	const currentSubContractorOptions = useMemo(() => {
		const subSupplierFacility = nullSafeGet(
			`${CONTRACTOR_PROPS.TO_WO}.subSupplierFacility`,
			formData
		);
		return subSupplierFacility ? [{ supplierFacility: subSupplierFacility }] : [];
	}, [formData]);

	return (
		<Modal
			visible={true}
			width="90%"
			title={titleText}
			footer={null}
			onCancel={onCancel}
			closable
			confirmLoading={creating || updating}
		>
			<Form
				layout="vertical"
				className="supplierForm"
				id={FORM_ID}
				style={{ width: 640, maxWidth: 640 }}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				<Form.Item name="updatedTitle" label="Title">
					<Input.TextArea
						autoSize
						showCount
						maxLength={120}
						style={{ width: '100%' }}
						size="large"
					/>
				</Form.Item>
				<Form.Item name="updatedDescription" label="Description">
					<OWRichTextEditor initialEditorState={initialDescription} editorRef={editorRef} />
				</Form.Item>
				<Form.Item
					required={required}
					name="supplierFacilityId"
					label="Subcontractor"
					rules={[
						{
							required,
							message: 'Please select a subcontractor',
						},
					]}
				>
					<OWAsyncSelect
						stateSlice={contractorPrivateNetworks}
						targetCollectionName={'contractorPrivateNetworksIndex'}
						fetchMultiple={(ids, targetCollectionName) => {}}
						extraOptions={currentSubContractorOptions}
						valueAccessor={(item) => nullSafeGet('supplierFacility.id', item)}
						labelAccessor={(item) =>
							nullSafeGet('supplierFacility.displayName', item) ||
							nullSafeGet('supplierFacility.name', item)
						}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchContractors(
								{ search: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(contractor) => (
							<Select.Option
								key={nullSafeGet('supplierFacility.id', contractor)}
								value={nullSafeGet('supplierFacility.id', contractor)}
							>
								{nullSafeGet('supplierFacility.displayName', contractor) ||
									nullSafeGet('supplierFacility.name', contractor)}
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
						allowClear
					/>
				</Form.Item>
				<Form.Item
					name="subContractorNte"
					label="Subcontractor NTE"
					rules={[
						...(clientNte
							? [
									{
										type: 'number',
										max: clientNte,
										message: isClientNteHidden
											? "Subcontractor NTE cannot exceed the client's NTE"
											: `Please enter NTE amount less than ${clientNte}`,
									},
							  ]
							: []),
					]}
				>
					<InputNumber
						max={isClientNteHidden ? undefined : clientNte}
						style={{ width: '100%' }}
						addonAfter={workOrderCurrency.id}
					/>
				</Form.Item>
			</Form>
			<ModalFormFooter
				okText={okText}
				cancelText="Cancel"
				loading={creating || updating}
				formId={FORM_ID}
				onCancelClick={onCancel}
			/>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	supplierFacilities: state.supplier_facilities,
	currentUser: state.session.currentUser,
	createErrors: state.buyer_contacts.createErrors,
	updateErrors: state.buyer_contacts.updateErrors,
	creating: state.work_orders.creating,
	updating: state.work_orders.updating,
	contractorPrivateNetworks: state.contractor_private_networks,
});

const mapDispatchToProps = (dispatch) => ({
	fetchContractors: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ContractorPrivateNetworksRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	subContractWorkOrder: (entity) => dispatch(subContractWorkOrderForSupplier(entity, 'id')),
	updateSubContractorInfo: (entity) => dispatch(updateContractWorkOrder(entity, 'id')),
	removeSubContractWorkOrder: (entity) =>
		dispatch(removeSubContractWorkOrderForSupplier(entity, 'id')),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubContractWorkOrderModalForm);
